import type { Me } from '@cliqcrm/contracts';
import { podeNaSessao } from '@/shared/sessao/contexto';

/*
 * O que a tela mostra para quem.
 *
 * Desde a #732 (ADR-0034), menus e botões seguem as permissões do `/v1/me`
 * (`usePode`, `podeNaSessao`), já com os ajustes da pessoa, e não o nome do
 * papel. Ficam aqui só as regras que não são permissão da organização: a
 * gestão de organizações e os Super Admins, que são do Super Admin, e a divisão
 * das automações entre a empresa e a Cliqfy, que a API decide pelas permissões
 * e por uma exceção do Super Admin.
 */

/** O que a tela sabe de quem está nela, para as regras que olham as permissões. */
export type QuemNaTela = Pick<Me, 'papel' | 'permissoes'>;

/**
 * Quem gere organizações (criar, completar, encerrar, reabrir e excluir): só o
 * Super Admin (emendas de 21/10 e de 26/10 ao ADR-0025). A operação e o
 * administrador do suporte faziam parte disso até ali. Espelha
 * `exigirGestaoDeOrganizacoes` da API, que recusa os outros do mesmo jeito.
 */
export function podeCriarOrganizacaoComo(me: Me | null): boolean {
  return me?.papel === 'super_admin';
}

/** Quem dá e tira o papel de Super Admin: só outro Super Admin (ADR-0025). */
export function geraSuperAdmins(papel: string | null | undefined): boolean {
  return papel === 'super_admin';
}

/**
 * Quem escolhe as rotinas do "Atualizar" de cada funil: só o Super Admin
 * (decisão de 28/10, ADR-0030). Espelha `exigirSuperAdminEmVigor` da API.
 */
export function configuraOAtualizar(papel: string | null): boolean {
  return papel === 'super_admin';
}

/**
 * Quem monta automação com SQL (os blocos `consulta_sql` e `para_cada`): quem
 * edita integração de dados, a Cliqfy (decisão de 19/10, #932). Espelha
 * `podeSql` de `apps/api/src/automacoes/automacoes.ts`.
 */
export function montaAutomacaoComSql(quem: QuemNaTela | null): boolean {
  return podeNaSessao(quem, 'integracao_de_dados', 'editar');
}

/**
 * Quem monta automação interna: quem cria ou edita automação e não monta SQL
 * (a empresa); o Super Admin monta os dois tipos (ADR-0025). Espelha
 * `podeInterna` da API.
 */
export function montaAutomacaoInterna(
  quem: QuemNaTela | null,
  acao: 'criar' | 'editar' = 'editar',
): boolean {
  return (
    podeNaSessao(quem, 'automacao', acao) &&
    (!montaAutomacaoComSql(quem) || quem?.papel === 'super_admin')
  );
}

/**
 * Quem edita uma automação deste tipo: o lado que o monta, com `automacao ×
 * editar`. É a régua de editar, mandar para teste, voltar para rascunho e
 * restaurar.
 */
export function montaOTipoDaAutomacao(quem: QuemNaTela | null, usaSql: boolean): boolean {
  return usaSql
    ? podeNaSessao(quem, 'automacao', 'editar') && montaAutomacaoComSql(quem)
    : montaAutomacaoInterna(quem);
}

/**
 * Quem põe esta automação no ar, pausa ou arquiva: quem tem a ação, e do lado
 * certo. A Cliqfy liga e arquiva só as com SQL; a empresa e o Super Admin,
 * qualquer uma (decisão de 19/10, #932).
 */
export function mexeNoArDaAutomacao(
  quem: QuemNaTela | null,
  acao: 'ativar' | 'remover',
  usaSql: boolean,
): boolean {
  return (
    podeNaSessao(quem, 'automacao', acao) &&
    (usaSql || !montaAutomacaoComSql(quem) || quem?.papel === 'super_admin')
  );
}
